import { Badge, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

import { useDisableUserMutation } from '../../../app/adminApiSlice'
import TrBtn from '../../../components/buttons/table/TrBtn'
import TypeToConfirmButton from '../../../components/buttons/TypeToConfirmButton'
import { useDeleteUserMutation } from '../../../app/authApiSlice'
import { useModal } from '../../../context/ModalContext'
import isPaymentDue from '../../../utils/membership/isPaymentDue'
import DeleteButton from '../../../components/buttons/table/DeleteButton'
import { Ban, CircleCheck } from 'lucide-react'
import ViewUserButton from '../../../components/buttons/table/ViewUserButton'
import MarkPaidBadge from '../../../components/buttons/MarkPaidBadge'

const MembersList = ({ members }) => {
  const navigate = useNavigate()
  const { openModal } = useModal()

  const [disableUser] = useDisableUserMutation()
  const [deleteUser, { isLoading: deleting }] = useDeleteUserMutation()

  const handleDisableUser = async user => {
    try {
      await disableUser(user).unwrap()
    } catch (error) {
      console.log(error)
    }
  }

  const handleViewUserClicked = id => navigate(`/admin/members/view-user/${id}`)

  const handleDeleteUser = async id => {
    try {
      await deleteUser({ id }).unwrap()

      toast.success('User deleted')
    } catch (error) {
      console.log(error)
      if (error?.data?.message === 'User is a club staff member') {
        openModal({
          title: <span className="text-danger">Failed to delete user</span>,
          body: (
            <>
              <p className="mb-0">
                This user cannot be deleted because they hold staff positions in one or more clubs.
              </p>
              <p className="mb-0">Please remove all staff positions before deleting this user.</p>
            </>
          ),
        })
      } else toast.error('An error occurred')
    }
  }

  let tableContent

  if (members.length < 1) {
    tableContent = (
      <tr>
        <td colSpan={6} className="text-center">
          No members found
        </td>
      </tr>
    )
  } else {
    tableContent = members.map(user => {
      const disabledClass = user.disabled ? 'danger' : 'success'

      let membership

      if (user.validUntil === undefined) {
        console.error('User has no validUntil date')
        membership = (
          <Badge className="fw-semibold fs-8" bg="warning">
            N/A
          </Badge>
        )
      } else if (isPaymentDue(user.validUntil)) {
        membership = <MarkPaidBadge userId={user.id} userName={user.name} />
      } else {
        membership = (
          <Badge className="fw-semibold fs-8" bg="success">
            Paid
          </Badge>
        )
      }

      return (
        <tr key={user.id}>
          <td>{user.name}</td>
          <td>{user.id}</td>
          <td>{user.email}</td>
          <td className={`${disabledClass}`}>
            <Badge className="fw-semibold fs-8" bg={disabledClass}>
              {user.disabled ? 'Disabled' : 'Active'}
            </Badge>
          </td>
          <td>
            <div className="ms-4">{membership}</div>
          </td>
          <td>
            {/*--- ACTION BUTTONS ---*/}
            <div className="d-flex gap-2 p-0 m-0 justify-content-center">
              {/* View user button */}
              <ViewUserButton action={() => handleViewUserClicked(user.id)} />

              {/* Disable/Activate user button */}
              <TrBtn text="Disable/Enable" action={() => handleDisableUser(user)}>
                {user.disabled ? <CircleCheck size={15} /> : <Ban size={15} />}
              </TrBtn>

              {/* Delete user button */}
              <TypeToConfirmButton
                action={() => handleDeleteUser(user.id)}
                isLoading={deleting}
                title="Delete user?"
                message="This action cannot be undone."
                buttonText="Delete"
                confirmMessage="Please type user email to confirm."
                confirmString={user.email}
              >
                <DeleteButton />
              </TypeToConfirmButton>
            </div>
          </td>
        </tr>
      )
    })
  }

  return (
    <div className="overflow-auto w-100 rounded-4 py-4 px-4 shadow-sm bg-white">
      <Table hover className="mb-0 mt-1" style={{ minWidth: '730px' }}>
        <thead>
          <tr>
            <th className="">Name</th>
            <th className="">GBM Num</th>
            <th className="">Email</th>
            <th className="">Status</th>
            <th>Membership</th>
            <th className=" text-center">Actions</th>
          </tr>
        </thead>
        <tbody>{tableContent}</tbody>
      </Table>
    </div>
  )
}

export default MembersList
